/* eslint-disable @typescript-eslint/naming-convention */

interface NormalizedProduct {
  variantId: string
  priceFloat: number
  title: string
  category: string
}

interface FoxsellBundleConfig {
  bundleId: string
  fixed: Product[]
  variantId: number
  groups: {
    title: string
    imageUrl: string
    cartImageUrl: string
    cartSubtitle: string
    products: Product[]
  }[]
}

interface Product {
  id: number
  title: string
  handle: string
  description: string
  published_at: string
  created_at: string
  vendor: string
  type: string
  tags: string[]
  price: number
  price_min: number
  price_max: number
  available: boolean
  price_varies: boolean
  compare_at_price: number | null
  compare_at_price_min: number
  compare_at_price_max: number
  compare_at_price_varies: boolean
  variants: {
    id: number
    title: string
    option1: string | null
    option2: string | null
    option3: string | null
    sku: string
    requires_shipping: boolean
    taxable: boolean
    featured_image: string | null
    available: boolean
    name: string
    public_title: string | null
    options: string[]
    price: number
    weight: number
    compare_at_price: number | null
    inventory_management: string | null
    barcode: string
    requires_selling_plan: boolean
    selling_plan_allocations: {
      price_adjustments: {
        position: number
        price: number
      }[]
      price: number
      compare_at_price: number
      per_delivery_price: number
      selling_plan_id: number
      selling_plan_group_id: string
    }[]
    quantity_rule: {
      min: number
      max: number | null
      increment: number
    }
  }[]
  images: string[]
  featured_image: string
  options: string[]
  media: {
    alt: string | null
    id: number
    position: number
    preview_image: {
      aspect_ratio: number
      height: number
      width: number
      src: string
    }
    aspect_ratio: number
    height: number
    media_type: string
    src: string
    width: number
    duration?: number
    sources?: {
      format: string
      height: number
      mime_type: string
      url: string
      width: number
    }[]
  }[]
  requires_selling_plan: boolean
  selling_plan_groups: {
    id: string
    name: string
    options: {
      name: string
      position: number
      values: string[]
    }[]
    selling_plans: {
      id: number
      name: string
      description: string | null
      options: {
        name: string
        position: number
        value: string
      }[]
      recurring_deliveries: boolean
      price_adjustments: {
        order_count: number | null
        position: number
        value_type: string
        value: number
      }[]
      checkout_charge: {
        value_type: string
        value: number
      }
    }[]
    app_id: string
  }[]
  content: string
}

interface FoxsellProduct {
  variantId: string
  category: string
  quantity: number
  type: 'product'
}
export default {
  name: 'routineFoxsellBundle',
  // Accepts either:
  // - raw metaobject JSON: first_bundle + bundle_list
  // - or pre-normalized arrays: fixed + groups (preferred, passed via data attributes)
  component({ config }: { config: FoxsellBundleConfig }) {
    const bundleId = config?.bundleId

    const groups = config?.groups
    const fixed = config?.fixed

    return {
      loading: false,
      bundleId,
      groups,
      fixed,
      selectedGroupIndex: 0,
      selectedProducts: [] as FoxsellProduct[],

      buildSelection() {
        const group = groups[this.selectedGroupIndex]
        const selection = [
          ...fixed.map((p) => this.fromShopifyProductToFoxsellProduct(p, 'Routine étape 1')),
          ...group.products.map((p) => this.fromShopifyProductToFoxsellProduct(p, 'Routine étape 2'))
        ]
        return selection
      },

      fromShopifyProductToFoxsellProduct(product: Product, category: string): FoxsellProduct {
        return {
          variantId: `${product.variants[0].id}`,
          category,
          quantity: 1,
          type: 'product'
        }
      },

      selectGroup(index: number) {
        this.selectedGroupIndex = index
        this.scrollToSelectedGroup()
        // Dispatch event for preview image sync
        window.dispatchEvent(new CustomEvent('routine-group-changed', { detail: { index } }))
      },

      scrollToSelectedGroup() {
        const alpine = this as unknown as { $nextTick: (fn: () => void) => void; $refs: Record<string, HTMLElement> }
        alpine.$nextTick(() => {
          const container = alpine.$refs.groupsContainer as HTMLElement
          const selectedButton = container?.querySelector(
            `[data-group-index="${this.selectedGroupIndex}"]`
          ) as HTMLElement
          if (container && selectedButton) {
            const containerRect = container.getBoundingClientRect()
            const buttonRect = selectedButton.getBoundingClientRect()
            const scrollLeft =
              selectedButton.offsetLeft - container.offsetLeft - containerRect.width / 2 + buttonRect.width / 2
            container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
          }
        })
      },

      prepareAddToCartData() {
        const products = this.buildSelection()
        const identifier = `${this.bundleId}_${Date.now()}`
        const selectedGroup = groups[this.selectedGroupIndex]
        const selectedGroupImageUrl = selectedGroup?.cartImageUrl || selectedGroup?.imageUrl || ''
        const selectedGroupSubtitle = selectedGroup?.cartSubtitle || null

        const properties: Record<string, string> = {
          '__foxsell:dynamic_add_on_bundle_id': identifier,
          '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(products)
        }

        // Add routine bundle image URL for line item display
        if (selectedGroupImageUrl) {
          properties._routine_bundle_image = selectedGroupImageUrl
        }

        // Add routine bundle subtitle for line item display
        if (selectedGroupSubtitle) {
          properties._routine_bundle_subtitle = selectedGroupSubtitle
        }

        const items = [
          {
            id: config.variantId,
            quantity: 1,
            properties
          }
        ]
        return { items }
      },

      async addToCart() {
        if (this.loading) return
        this.loading = true
        try {
          const payload = this.prepareAddToCartData()

          if (!payload || !payload.items?.length) {
            console.warn('FoxSell: missing items; aborting add to cart.')
            return
          }
          await window.liquidAjaxCart.add(payload)
        } catch (error) {
          console.error('Error adding to cart:', error)
        } finally {
          this.loading = false
        }
      }
    }
  }
}
